/**
 * GeoGate — region restriction (DISABLED for beta, 2026-05-12).
 *
 * The region restriction is intentionally short-circuited below — this
 * component currently renders its children unconditionally. The geo-check
 * edge function (supabase/functions/geo-check/index.ts) and its
 * ALLOWED_COUNTRIES constant are left in place untouched.
 *
 * To re-enable after beta:
 *   1. Delete the early `return <>{children}</>;` at the top of the
 *      component below.
 *   2. Uncomment the original body that follows (everything inside the
 *      /* --- ORIGINAL BODY --- *\/ block).
 *   3. If the allowed-region set has changed, edit ALLOWED_COUNTRIES in
 *      supabase/functions/geo-check/index.ts and redeploy the function.
 *
 * Note: the gate caches its result in sessionStorage under the key
 * `compaire-geo-result`. If re-enabling and users report stale access,
 * they may need to clear that key (or close/reopen the tab) to pick up
 * the new gate behavior.
 *
 * The imports below are unused while the gate is disabled but kept
 * intact so the restoration path is just an uncomment.
 */

import { useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Shield, MapPin } from "lucide-react";

interface GeoGateProps {
  children: ReactNode;
}

const GEO_CACHE_KEY = "compaire-geo-result";

interface CachedGeoResult {
  allowed: boolean;
  country: string;
}

const GeoGate = ({ children }: GeoGateProps) => {
  // Region restriction disabled for beta (2026-05-12). See header comment
  // for re-enable instructions.
  return <>{children}</>;

  /* --- ORIGINAL BODY ---
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);
  const [country, setCountry] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check sessionStorage cache first
    try {
      const cached = sessionStorage.getItem(GEO_CACHE_KEY);
      if (cached) {
        const result: CachedGeoResult = JSON.parse(cached);
        setIsAllowed(result.allowed);
        setCountry(result.country);
        setIsLoading(false);
        return;
      }
    } catch {}

    const checkGeo = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('geo-check');

        if (error) {
          console.error('Geo check error:', error);
          setIsAllowed(true);
          return;
        }

        const result: CachedGeoResult = {
          allowed: data?.allowed ?? true,
          country: data?.country || 'Unknown',
        };

        // Cache result for the session
        try {
          sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify(result));
        } catch {}

        setCountry(result.country);
        setIsAllowed(result.allowed);
      } catch (err) {
        console.error('Geo check failed:', err);
        setIsAllowed(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkGeo();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground text-sm">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <Shield className="h-8 w-8 text-destructive" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              Access restricted
            </h1>
            <p className="text-muted-foreground">
              Compaire is currently only available in select regions.
            </p>
          </div>

          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>Your detected location: <strong>{country}</strong></span>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            If you believe this is an error, please contact support.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
  --- END ORIGINAL BODY --- */
};

export default GeoGate;
